import React, { useEffect, useState, useRef, useMemo } from 'react';
import { Amplify } from 'aws-amplify';
import { getCurrentUser, signOut, fetchAuthSession } from '@aws-amplify/auth';
import { Hub } from 'aws-amplify/utils';
import { BrowserRouter, Routes, Route, Link } from 'react-router-dom';
import { ApolloClient, InMemoryCache, ApolloProvider, useQuery, gql } from '@apollo/client';
import './styles/variables.css';
import './styles/global.css';
import hoaImage from './images/hoa-property.jpg';
import mapImage from './images/map-view.jpg';
import Amenities from './pages/public/Amenities';
import Contact from './pages/public/Contact';
import Login from './pages/public/Login';
import Documents from './pages/public/Documents';
import Profile from './pages/profile/Profile';
import Billing from './pages/billing/Billing';
import Board from './pages/board/Board';
import amplifyConfig from './services/amplify-config';
import { createAuthLink } from 'aws-appsync-auth-link';
import { createSubscriptionHandshakeLink } from 'aws-appsync-subscription-link';
import { ApolloLink } from '@apollo/client';
import { BULLETINS_BY_DATE, PROFILE_BY_COGNITO_ID } from './queries/queries';
import ReactQuill from 'react-quill';
import MenuState from './components/menu/MenuState';
import PrivacyBanner from './components/PrivacyBanner';
import DatabaseReset from './components/dev/DatabaseReset'
import useEnsureMyProfile from './hooks/useEnsureMyProfile';
import 'react-quill/dist/quill.bubble.css';

const amplifyConfiguration = {
  ...amplifyConfig,
  oauth: {
    ...amplifyConfig.oauth,
    domain: process.env.REACT_APP_AUTH_DOMAIN,
    redirectSignIn: process.env.REACT_APP_REDIRECT_SIGN_IN,
    redirectSignOut: process.env.REACT_APP_REDIRECT_SIGN_OUT,
  },
  API: {
    ...(amplifyConfig.API ?? {}),
    REST: {
      ...(amplifyConfig.API?.REST ?? {}),
      cognitoGroupManagement: {
        endpoint: process.env.REACT_APP_API_ENDPOINT,
        region: process.env.REACT_APP_AWS_REGION
      }
    }
  }
};



const url = amplifyConfig.aws_appsync_graphqlEndpoint;
const region = amplifyConfig.aws_appsync_region;
const BOARD_GROUP = process.env.REACT_APP_BOARD_GROUP_NAME;

export function useAuthState() {
  const [user, setUser] = useState(null);
  const [userGroups, setUserGroups] = useState([]);
  const refreshId = useRef(0);

  useEffect(() => {
    let active = true;
    let authCompletionObserved = false;

    const clearAuth = () => {
      refreshId.current += 1;
      if (active) {
        setUser(null);
        setUserGroups([]);
      }
    };

    const refreshAuth = async () => {
      const currentRefreshId = ++refreshId.current;

      try {
        const currentUser = await getCurrentUser();
        if (active && currentRefreshId === refreshId.current) {
          setUser(currentUser);
          setUserGroups([]);
        }

        try {
          const session = await fetchAuthSession();
          const groups = session?.tokens?.idToken?.payload?.['cognito:groups'] || [];

          if (active && currentRefreshId === refreshId.current) {
            setUserGroups(groups);
          }
        } catch (error) {
          // A valid current user remains authenticated even if group lookup fails.
        }
      } catch (error) {
        if (active && currentRefreshId === refreshId.current) {
          setUser(null);
          setUserGroups([]);
        }
      }
    };

    const unsubscribe = Hub.listen('auth', ({ payload }) => {
      if (payload.event === 'signInWithRedirect' || payload.event === 'signedIn') {
        authCompletionObserved = true;
        refreshAuth();
      } else if (payload.event === 'signedOut') {
        clearAuth();
      }
    });

    Amplify.configure(amplifyConfiguration);
    if (!authCompletionObserved) {
      refreshAuth();
    }

    return () => {
      active = false;
      refreshId.current += 1;
      unsubscribe();
    };
  }, []);

  return { user, userGroups };
}

function App() {
  const { user, userGroups } = useAuthState();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const menuRef = useRef(null);

  const auth = {
    type: user ? amplifyConfig.aws_appsync_authenticationType : 'API_KEY',
    apiKey: process.env.REACT_APP_API_KEY,
    jwtToken: async () => {
      if (user) {
        try {
          const session = await fetchAuthSession();
          return session?.tokens?.idToken?.toString() || '';
        } catch (error) {
          console.warn('Error fetching auth session:', error.message);
          return '';
        }
      }
      return null;
    },
  };

  const authenticatedClient = new ApolloClient({
    link: ApolloLink.from([
      createAuthLink({ url, region, auth }),
      createSubscriptionHandshakeLink({ url, region, auth })
    ]),
    cache: new InMemoryCache(),
    defaultOptions: {
      watchQuery: {
        fetchPolicy: 'cache-and-network',
        errorPolicy: 'ignore',
      },
      query: {
        fetchPolicy: 'network-only',
        errorPolicy: 'all',
      },
    }
  });

  const publicClient = new ApolloClient({
    uri: url,
    cache: new InMemoryCache(),
    headers: {
      'x-api-key': process.env.REACT_APP_API_KEY
    }
  });

  useEffect(() => {
    function handleClickOutside(event) {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setIsMenuOpen(false);
      }
    }

    function handleEscape(event) {
      if (event.key === 'Escape') {
        setIsMenuOpen(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleEscape);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEscape);
    };
  }, []);

  async function handleSignOut() {
    try {
      await signOut();
    } catch (error) {
      console.error('Error signing out: ', error);
    }
  }

  const renderMenuItems = () => {
    const menuItems = [
      { label: 'Profile', path: '/profile' },
      { label: 'Board', path: '/board', group: BOARD_GROUP },
      { label: 'Amenities', path: '/amenities' },
      { label: 'Documents', path: '/documents' },
      { label: 'Contact', path: '/contact' }
    ];

    return menuItems
      .filter(item => !item.group || userGroups.includes(item.group))
      .map((item, index) => (
        <Link key={index} to={item.path} onClick={() => setIsMenuOpen(false)}>
          {item.label}
        </Link>
      ));
  };

  const cognitoId = user?.userId || user?.username;

  const HomePage = () => {
    // Determine user roles based on profile data and groups
    const { loading: profileLoading, error: profileError, data: profileData } = useQuery(PROFILE_BY_COGNITO_ID, {
      variables: { cognitoID: cognitoId },
      skip: !user,
      client: authenticatedClient
    });

    // Get profile data
    const profile = profileData?.profileByCognitoID?.items[0];

    // Determine user roles based on profile data
    const isTenant = profile?.tenantAt !== null && profile?.tenantAt !== undefined;
    const isOwner = profile?.ownedProperties?.items?.length > 0;
    const isBoard = userGroups.includes(BOARD_GROUP);

    // Fetch all bulletins with a high limit
    const { loading: bulletinsLoading, error: bulletinsError, data: bulletinsData } = useQuery(BULLETINS_BY_DATE, {
      variables: {
        limit: 50, // High limit to ensure we get all relevant bulletins
        type: "BULLETIN",
        sortDirection: "DESC"
      },
      client: user ? authenticatedClient : publicClient
    });

    // Filter bulletins client-side based on user roles
    const filteredBulletins = useMemo(() => {
      const allBulletins = bulletinsData?.bulletinsByDate?.items || [];

      // For unauthenticated users, show only PUBLIC bulletins
      if (!user) {
        return allBulletins
          .filter(bulletin => bulletin.audience && bulletin.audience.includes("PUBLIC"))
          .slice(0, 3); // Limit to 3 for public users
      }

      // For board members, show all bulletins
      if (isBoard) {
        return allBulletins.slice(0, 10); // Limit to 10 for board members
      }

      // For other authenticated users, filter based on roles
      return allBulletins
        .filter(bulletin => {
          if (!bulletin.audience) return false;

          if (bulletin.audience.includes("PUBLIC")) return true;
          if (isTenant && bulletin.audience.includes("RESIDENTS")) return true;
          if (isOwner && bulletin.audience.includes("OWNERS")) return true;

          return false;
        })
        .slice(0, 10); // Limit to 10 for authenticated users
    }, [bulletinsData, user, isBoard, isTenant, isOwner]);

    const isLoading = bulletinsLoading || (user && !isBoard && profileLoading);

    return (
      <main className="content">
        <div className="main-content">
          <div className="image-map-container">
            <div className="hoa-image">
              <img src={hoaImage} alt="HOA Properties" />
            </div>
            <div className="map-placeholder">
              <img src={mapImage} alt="Map View" />
            </div>
          </div>
          <div className="bulletins">
            <h2>Recent Bulletins</h2>
            {isLoading && <p>Loading bulletins...</p>}
            {(bulletinsError || (user && !isBoard && profileError)) && (
              <div>
                <p>Error loading bulletins. Please try again later.</p>
                {bulletinsError && <p>Bulletin error: {bulletinsError.message}</p>}
                {profileError && <p>Profile error: {profileError.message}</p>}
              </div>
            )}
            {!isLoading && !bulletinsError && !(user && !isBoard && profileError) && (
              <ul className="bulletin-list">
                {filteredBulletins.length > 0 ? (
                  filteredBulletins.map(bulletin => (
                    <li key={bulletin.id} className="bulletin-card">
                      <h3>{bulletin.title}</h3>
                      <ReactQuill
                        value={bulletin.content}
                        readOnly={true}
                        theme="bubble"
                        modules={{ toolbar: false }}
                      />
                      <p className="bulletin-date">
                        {new Date(bulletin.createdAt).toLocaleDateString()}
                      </p>
                      {user && (
                        <p className="bulletin-audience" style={{ fontSize: '0.8em', color: '#666' }}>
                          Audience: {bulletin.audience.join(', ')}
                        </p>
                      )}
                    </li>
                  ))
                ) : (
                  <p>No bulletins to display</p>
                )}
              </ul>
            )}
          </div>
        </div>
      </main>
    );
  };

  // AppContent component to enable useQuery inside ApolloProvider
  const AppContent = () => {
    const {
      profile: appProfile,
      initializationError,
      retryInitialization
    } = useEnsureMyProfile(user, authenticatedClient);
    const isOwner = appProfile?.ownedProperties?.items?.length > 0;

    return (
      <div className="App">
        <header className="top-bar">
          <Link to="/" className="site-title">
            <h1>Lexington Commons HOA</h1>
          </Link>
          {user ? (
            <MenuState
              userGroups={userGroups}
              onSignOut={handleSignOut}
              renderMenuItems={renderMenuItems}
            />
          ) : (
            <>
              <nav className="desktop-menu">
                <Link to="/amenities">Amenities</Link>
                <Link to="/documents">Documents</Link>
                <Link to="/contact">Contact</Link>
                <Login />
              </nav>
              <div className="mobile-header-actions">
                <Login />
                <div className="hamburger-menu" ref={menuRef}>
                  <button
                    type="button"
                    className="hamburger-button"
                    aria-label={`${isMenuOpen ? 'Close' : 'Open'} navigation menu`}
                    aria-expanded={isMenuOpen}
                    onClick={() => setIsMenuOpen(!isMenuOpen)}
                  >
                    <span aria-hidden="true" className="hamburger-icon" />
                  </button>
                  {isMenuOpen && (
                    <nav className="dropdown-menu" aria-label="Mobile navigation">
                      <Link to="/amenities" onClick={() => setIsMenuOpen(false)}>Amenities</Link>
                      <Link to="/documents" onClick={() => setIsMenuOpen(false)}>Documents</Link>
                      <Link to="/contact" onClick={() => setIsMenuOpen(false)}>Contact</Link>
                    </nav>
                  )}
                </div>
              </div>
            </>
          )}
        </header>
        {initializationError && (
          <div className="profile-initialization-error" role="alert">
            <span>We could not initialize your profile.</span>
            <button type="button" onClick={retryInitialization}>Retry</button>
          </div>
        )}
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/profile" element={<Profile cognitoId={cognitoId} />} />
          <Route path="/billing" element={<Billing cognitoId={cognitoId} />} />
          <Route path="/amenities" element={<Amenities />} />
          <Route path="/documents" element={<Documents user={user} userGroups={userGroups} isOwner={isOwner} />} />
          <Route path="/board" element={<Board userGroups={userGroups} user={user} />} />
          <Route path="/contact" element={<Contact />} />
        </Routes>
        <PrivacyBanner />
      </div>
    );
  };

  return (
    <ApolloProvider client={user ? authenticatedClient : publicClient}>
      <BrowserRouter>
        <AppContent />
      </BrowserRouter>
    </ApolloProvider>
  );
}

export default App;
